// Survival damage (PLR-3 basic, PLR-9, E-8). PURE (ENG-1).
//
// M4.3 covers the environmental damage sources — falling, drowning — and the
// damage-mitigation formula skeleton (armour/EPF terms are 0 until gear lands in
// M17). Mob melee/knockback join in M5.2.
import { SURVIVAL, BURN, COMBAT } from '../config';
import type { Player } from './player';

export interface FallCtx {
  /** Body is in water (any depth) — negates fall damage entirely (E-8). */
  inWater?: boolean;
  /** Block landed on (name) — hay ×0.2, slime/powder-snow negate (E-8). */
  landBlock?: string;
  /** Slow-falling effect zeroes fall damage. */
  slowFalling?: boolean;
  /** Feather-falling enchant protection factor (EPF, 0..). */
  featherEPF?: number;
}

/** Fall damage in health points (2 = 1 heart): `floor(distance − 3)`, modified
 *  by the landing block and effects (PLR-9, E-8). */
export function fallDamage(fallDistance: number, ctx: FallCtx = {}): number {
  if (ctx.inWater || ctx.slowFalling || ctx.landBlock === 'water') return 0;
  let dmg = Math.floor(fallDistance - SURVIVAL.FALL_SAFE_BLOCKS);
  if (dmg <= 0) return 0;
  if (ctx.landBlock === 'slime_block' || ctx.landBlock === 'powder_snow' || ctx.landBlock === 'honey_block') return 0;
  if (ctx.landBlock === 'hay_block') dmg = Math.floor(dmg * 0.2);
  if (ctx.featherEPF) dmg = Math.max(0, Math.round(dmg * (1 - Math.min(20, ctx.featherEPF) * 0.04)));
  return Math.max(0, dmg);
}

/**
 * PLR-3 mitigation: `final = base·(1 − armorTerm/25)·(1 − min(EPF,cap)/25)`.
 * `armorTerm = clamp(armor − base/(2+toughness/4), armor/5, 20)` (the toughness
 * curve softens big hits); the EPF term is filled by the M17 gear engine. With no
 * armour/enchants this is the identity. See `game/gear.ts` for the gear-driven
 * callers (`absorbWithGear`).
 */
export function mitigate(base: number, armor = 0, toughness = 0, epf = 0): number {
  const armorTerm = Math.min(20, Math.max(armor / 5, armor - base / (2 + toughness / 4)));
  const epfTerm = Math.min(epf, COMBAT.EPF_CAP) / COMBAT.EPF_DIVISOR;
  return base * (1 - armorTerm / 25) * (1 - epfTerm);
}

/**
 * Advance the air supply one tick and return any drowning damage. Underwater the
 * bar drains; at 0 it deals 2 (one heart) every DROWN_TICKS; out of water it
 * refills quickly. Mutates `player.air` and `player.drownTimer`.
 */
export function tickAir(player: Player, headInWater: boolean): number {
  if (!player.caps.survivalHunger) { player.air = SURVIVAL.MAX_AIR; return 0; }
  if (headInWater) {
    if (player.air > 0) {
      player.air = Math.max(0, player.air - 1);
      return 0;
    }
    player.drownTimer++;
    if (player.drownTimer >= SURVIVAL.DROWN_TICKS) {
      player.drownTimer = 0;
      return 2;
    }
    return 0;
  }
  player.air = Math.min(SURVIVAL.MAX_AIR, player.air + 4);
  player.drownTimer = 0;
  return 0;
}

/**
 * Advance the on-fire / lava-contact state one tick and return any damage (M7.2).
 * Standing in lava sets a 4 s burn and deals LAVA_DAMAGE on a cadence; the burn
 * then keeps dealing FIRE_DAMAGE after the player leaves. Water extinguishes it.
 * Mutates `player.fireTicks` and its two damage-cadence timers.
 */
export function tickFire(player: Player, inLava: boolean, inWater: boolean): number {
  if (!player.caps.survivalHunger) { player.fireTicks = 0; return 0; }
  if (inWater) { player.fireTicks = 0; player.fireDamageTimer = 0; player.lavaDamageTimer = 0; return 0; }
  let damage = 0;
  if (inLava) {
    player.fireTicks = Math.max(player.fireTicks, BURN.LAVA_FIRE_TICKS);
    if (++player.lavaDamageTimer >= BURN.LAVA_DAMAGE_INTERVAL) { player.lavaDamageTimer = 0; damage += BURN.LAVA_DAMAGE; }
  } else {
    player.lavaDamageTimer = 0;
    if (player.fireTicks > 0) {
      player.fireTicks--;
      if (++player.fireDamageTimer >= BURN.FIRE_DAMAGE_INTERVAL) { player.fireDamageTimer = 0; damage += BURN.FIRE_DAMAGE; }
    } else {
      player.fireDamageTimer = 0;
    }
  }
  return damage;
}
